import { makeStyles } from "@material-ui/core";

// ─── Color tokens ────────────────────────────────────────────────────────────
export const CAT_YELLOW = '#FFCD11';
export const CAT_BLACK = '#1A1A1A';
export const CAT_DARK = '#2C2C2C';
export const CAT_GRAY = '#F5F5F5';
export const CAT_BORDER = '#E0E0E0';
export const CAT_TEXT = '#3D3D3D';

// ─── Styles ──────────────────────────────────────────────────────────────────
export const useStyles = makeStyles(() => ({
  // ── Keyframes (injected via @global)
  '@global': {
    '@keyframes gradientShift': {
      '0%': { backgroundPosition: '0% 50%' },
      '50%': { backgroundPosition: '100% 50%' },
      '100%': { backgroundPosition: '0% 50%' },
    },
    '@keyframes floatDot': {
      '0%, 100%': { transform: 'translateY(0px) scale(1)', opacity: 0.15 },
      '50%': { transform: 'translateY(-30px) scale(1.1)', opacity: 0.3 },
    },
    '@keyframes bounceArrow': {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(10px)' },
    },
    '@keyframes fadeInUp': {
      from: { opacity: 0, transform: 'translateY(30px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    '@keyframes pulse': {
      '0%, 100%': { boxShadow: `0 0 0 0 ${CAT_YELLOW}80` },
      '50%': { boxShadow: `0 0 0 8px ${CAT_YELLOW}00` },
    },
    '@keyframes spin': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    '@keyframes flowDot': {
      '0%': { left: '-10px', opacity: 0 },
      '10%': { opacity: 1 },
      '90%': { opacity: 1 },
      '100%': { left: 'calc(100% + 10px)', opacity: 0 },
    },
    '@keyframes nodeGlow': {
      '0%, 100%': { boxShadow: `0 0 0 0 ${CAT_YELLOW}40`, transform: 'scale(1)' },
      '50%': { boxShadow: `0 0 18px 4px ${CAT_YELLOW}55`, transform: 'scale(1.06)' },
    },
    // ── Sequential flow animations (10s cycle, 5 nodes)
    '@keyframes seqNode1': {
      '0%':   { boxShadow: `0 0 32px 10px ${CAT_YELLOW}C0`, transform: 'scale(1.12)' },
      '5%':   { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '95%':  { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '100%': { boxShadow: `0 0 32px 10px ${CAT_YELLOW}C0`, transform: 'scale(1.12)' },
    },
    '@keyframes seqNode2': {
      '0%':   { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '23%':  { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '26%':  { boxShadow: `0 0 32px 10px ${CAT_YELLOW}C0`, transform: 'scale(1.12)' },
      '31%':  { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '100%': { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
    },
    '@keyframes seqNode3': {
      '0%':   { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '48%':  { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '51%':  { boxShadow: `0 0 32px 10px ${CAT_YELLOW}C0`, transform: 'scale(1.12)' },
      '56%':  { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '100%': { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
    },
    '@keyframes seqNode4': {
      '0%':   { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '73%':  { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '76%':  { boxShadow: `0 0 32px 10px ${CAT_YELLOW}C0`, transform: 'scale(1.12)' },
      '81%':  { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '100%': { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
    },
    '@keyframes seqNode5': {
      '0%':   { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '97%':  { boxShadow: `0 0 0 0 ${CAT_YELLOW}00`, transform: 'scale(1)' },
      '100%': { boxShadow: `0 0 32px 10px ${CAT_YELLOW}C0`, transform: 'scale(1.12)' },
    },
    '@keyframes seqDot1': {
      '0%':   { 'offset-distance': '0%', opacity: 0 },
      '2%':   { 'offset-distance': '0%', opacity: 1 },
      '23%':  { 'offset-distance': '100%', opacity: 1 },
      '25%':  { 'offset-distance': '100%', opacity: 0 },
      '100%': { 'offset-distance': '100%', opacity: 0 },
    },
    '@keyframes seqDot2': {
      '0%':   { 'offset-distance': '0%', opacity: 0 },
      '27%':  { 'offset-distance': '0%', opacity: 0 },
      '28%':  { 'offset-distance': '0%', opacity: 1 },
      '48%':  { 'offset-distance': '100%', opacity: 1 },
      '50%':  { 'offset-distance': '100%', opacity: 0 },
      '100%': { 'offset-distance': '100%', opacity: 0 },
    },
    '@keyframes seqDot3': {
      '0%':   { 'offset-distance': '0%', opacity: 0 },
      '52%':  { 'offset-distance': '0%', opacity: 0 },
      '53%':  { 'offset-distance': '0%', opacity: 1 },
      '73%':  { 'offset-distance': '100%', opacity: 1 },
      '75%':  { 'offset-distance': '100%', opacity: 0 },
      '100%': { 'offset-distance': '100%', opacity: 0 },
    },
    '@keyframes seqDot4': {
      '0%':   { 'offset-distance': '0%', opacity: 0 },
      '77%':  { 'offset-distance': '0%', opacity: 0 },
      '78%':  { 'offset-distance': '0%', opacity: 1 },
      '98%':  { 'offset-distance': '100%', opacity: 1 },
      '100%': { 'offset-distance': '100%', opacity: 0 },
    },
    '@keyframes seqPath1': {
      '0%':   { opacity: 0 },
      '2%':   { opacity: 0.65 },
      '23%':  { opacity: 0.65 },
      '25%':  { opacity: 0 },
      '100%': { opacity: 0 },
    },
    '@keyframes seqPath2': {
      '0%':   { opacity: 0 },
      '27%':  { opacity: 0 },
      '28%':  { opacity: 0.65 },
      '48%':  { opacity: 0.65 },
      '50%':  { opacity: 0 },
      '100%': { opacity: 0 },
    },
    '@keyframes seqPath3': {
      '0%':   { opacity: 0 },
      '52%':  { opacity: 0 },
      '53%':  { opacity: 0.65 },
      '73%':  { opacity: 0.65 },
      '75%':  { opacity: 0 },
      '100%': { opacity: 0 },
    },
    '@keyframes seqPath4': {
      '0%':   { opacity: 0 },
      '77%':  { opacity: 0 },
      '78%':  { opacity: 0.65 },
      '98%':  { opacity: 0.65 },
      '100%': { opacity: 0 },
    },
  },

  // ── Root
  root: {
    fontFamily: "'Inter', 'Roboto', sans-serif",
    background: '#FAFAFA',
    minHeight: '100vh',
    overflowX: 'hidden',
  },

  // ── Hero
  hero: {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: `linear-gradient(135deg, ${CAT_BLACK} 0%, #2C2C2C 40%, #1a1a2e 100%)`,
    backgroundSize: '300% 300%',
    animation: 'gradientShift 12s ease infinite',
    overflow: 'hidden',
    padding: '60px 40px',
  },
  heroBgDot: {
    position: 'absolute',
    borderRadius: '50%',
    background: CAT_YELLOW,
    opacity: 0.12,
    animation: 'floatDot 6s ease-in-out infinite',
  },
  heroGrid: {
    position: 'relative',
    zIndex: 2,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px',
    alignItems: 'center',
    maxWidth: 1200,
    margin: '0 auto',
    width: '100%',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  heroLeft: {
    animation: 'fadeInUp 0.8s ease forwards',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: `${CAT_YELLOW}20`,
    border: `1px solid ${CAT_YELLOW}40`,
    borderRadius: 20,
    padding: '4px 14px',
    marginBottom: 20,
  },
  heroBadgeDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: CAT_YELLOW,
    animation: 'pulse 2s ease infinite',
  },
  heroBadgeText: {
    color: CAT_YELLOW,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  heroHeading: {
    color: '#FFFFFF',
    fontWeight: 800,
    fontSize: '3rem',
    lineHeight: 1.15,
    marginBottom: 20,
    '@media (max-width: 900px)': {
      fontSize: '2.2rem',
    },
  },
  heroHeadingAccent: {
    color: CAT_YELLOW,
    display: 'block',
  },
  heroDescription: {
    color: '#BDBDBD',
    fontSize: 17,
    lineHeight: 1.7,
    marginBottom: 36,
    maxWidth: 480,
  },
  heroBtnRow: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  btnPrimary: {
    background: CAT_YELLOW,
    color: CAT_BLACK,
    fontWeight: 700,
    fontSize: 15,
    padding: '12px 32px',
    borderRadius: 8,
    textTransform: 'none',
    boxShadow: `0 4px 20px ${CAT_YELLOW}50`,
    transition: 'all 0.25s ease',
    '&:hover': {
      background: '#FFD740',
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 28px ${CAT_YELLOW}70`,
    },
  },
  btnSecondary: {
    border: '1.5px solid rgba(255,255,255,0.35)',
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: 15,
    padding: '12px 32px',
    borderRadius: 8,
    textTransform: 'none',
    background: 'transparent',
    transition: 'all 0.25s ease',
    '&:hover': {
      background: 'rgba(255,255,255,0.08)',
      borderColor: 'rgba(255,255,255,0.6)',
      transform: 'translateY(-2px)',
    },
  },
  heroRight: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    animation: 'fadeInUp 1s ease 0.2s both',
  },
  heroIllustration: {
    width: '100%',
    maxWidth: 420,
    aspectRatio: '4/3',
    background: 'rgba(255,255,255,0.04)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    padding: 32,
    backdropFilter: 'blur(8px)',
  },
  heroIllustrationCircle: {
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${CAT_YELLOW}30, ${CAT_YELLOW}10)`,
    border: `3px solid ${CAT_YELLOW}50`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroIllustrationIcon: {
    fontSize: 56,
    color: CAT_YELLOW,
  },
  heroIllustrationStats: {
    display: 'flex',
    gap: 24,
    marginTop: 8,
  },
  heroStat: {
    textAlign: 'center',
  },
  heroStatNum: {
    color: CAT_YELLOW,
    fontSize: 22,
    fontWeight: 800,
    display: 'block',
  },
  heroStatLabel: {
    color: '#9E9E9E',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },

  // ── Animated flow illustration
  flowCard: {
    width: '100%',
    maxWidth: 460,
    background: 'rgba(255,255,255,0.04)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: 24,
    padding: '32px 28px',
    backdropFilter: 'blur(8px)',
  },
  flowTitle: {
    color: '#9E9E9E',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase' as const,
    textAlign: 'center' as const,
    marginBottom: 28,
  },
  flowRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginBottom: 24,
  },
  flowNode: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 8,
    minWidth: 72,
  },
  flowIconWrap: {
    width: 60,
    height: 60,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${CAT_YELLOW}25, ${CAT_YELLOW}08)`,
    border: `2px solid ${CAT_YELLOW}50`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'nodeGlow 3s ease-in-out infinite',
  },
  flowNodeIcon: { fontSize: 28, color: CAT_YELLOW },
  flowNodeLabel: { color: '#9E9E9E', fontSize: 11, fontWeight: 600, textAlign: 'center' as const },
  flowConnector: {
    flex: 1,
    position: 'relative' as const,
    height: 2,
    background: 'rgba(255,255,255,0.12)',
    margin: '0 4px',
    marginTop: 29,
    overflow: 'hidden',
  },
  flowDot: {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: CAT_YELLOW,
    boxShadow: `0 0 8px 2px ${CAT_YELLOW}80`,
    animation: 'flowDot 1.8s linear infinite',
  },
  flowTagRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 6,
    flexWrap: 'wrap' as const,
    marginBottom: 20,
  },
  flowTag: {
    background: `${CAT_YELLOW}18`,
    border: `1px solid ${CAT_YELLOW}40`,
    borderRadius: 20,
    padding: '3px 10px',
    fontSize: 11,
    color: CAT_YELLOW,
    fontWeight: 600,
  },

  scrollIndicator: {
    position: 'absolute',
    bottom: 32,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    zIndex: 3,
  },
  scrollText: {
    color: '#9E9E9E',
    fontSize: 12,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  scrollArrow: {
    color: CAT_YELLOW,
    animation: 'bounceArrow 1.5s ease-in-out infinite',
    fontSize: 28,
  },

  // ── Section wrapper
  section: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '72px 24px',
  },
  sectionLabel: {
    display: 'inline-block',
    background: `${CAT_YELLOW}25`,
    color: CAT_BLACK,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    padding: '4px 14px',
    borderRadius: 20,
    marginBottom: 12,
    border: `1px solid ${CAT_YELLOW}60`,
  },
  sectionTitle: {
    fontWeight: 800,
    fontSize: '2rem',
    color: CAT_BLACK,
    marginBottom: 8,
  },
  sectionSubtitle: {
    color: '#757575',
    fontSize: 16,
    marginBottom: 40,
  },

  // ── FAQ
  faqContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  faqAccordion: {
    borderRadius: '10px !important',
    border: `1px solid ${CAT_BORDER}`,
    boxShadow: 'none !important',
    '&:before': { display: 'none' },
    '&.Mui-expanded': {
      margin: '0 !important',
      border: `1.5px solid ${CAT_YELLOW}`,
    },
  },
  faqSummary: {
    fontWeight: 600,
    fontSize: 15,
    color: CAT_BLACK,
    '&.Mui-expanded': { color: CAT_BLACK },
  },
  faqDetails: {
    color: CAT_TEXT,
    fontSize: 14.5,
    lineHeight: 1.75,
    paddingTop: 0,
    paddingBottom: 16,
  },

  // ── Onboarding steps
  stepsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 40,
  },
  stepCard: {
    border: `1.5px solid ${CAT_BORDER}`,
    borderRadius: 12,
    padding: '20px 24px',
    cursor: 'pointer',
    transition: 'all 0.22s ease',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    '&:hover': {
      borderColor: CAT_YELLOW,
      boxShadow: `0 4px 20px ${CAT_YELLOW}25`,
      transform: 'translateY(-2px)',
    },
  },
  stepCardActive: {
    borderColor: CAT_YELLOW,
    background: `${CAT_YELLOW}08`,
    boxShadow: `0 4px 20px ${CAT_YELLOW}30`,
  },
  stepIconBox: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: `${CAT_YELLOW}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'background 0.2s',
  },
  stepIconBoxActive: {
    background: CAT_YELLOW,
  },
  stepIcon: {
    color: CAT_BLACK,
    fontSize: 22,
  },
  stepCardBody: {
    flex: 1,
  },
  stepNum: {
    fontSize: 11,
    fontWeight: 700,
    color: '#9E9E9E',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  stepTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: CAT_BLACK,
    marginBottom: 4,
  },
  stepDesc: {
    fontSize: 13,
    color: '#757575',
    lineHeight: 1.6,
  },

  // ── Step detail panel
  stepPanel: {
    border: `1.5px solid ${CAT_YELLOW}`,
    borderRadius: 12,
    background: '#FFFFFF',
    overflow: 'hidden',
    boxShadow: `0 6px 24px ${CAT_YELLOW}22`,
  },
  stepPanelHeader: {
    background: CAT_BLACK,
    padding: '20px 28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  stepPanelTitle: {
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  stepPanelTitleIcon: {
    color: CAT_YELLOW,
    fontSize: 24,
  },
  stepPanelBody: {
    padding: '28px',
  },

  // ── Info rows inside panels
  infoRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 10,
    alignItems: 'flex-start',
  },
  infoBullet: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: CAT_YELLOW,
    marginTop: 8,
    flexShrink: 0,
  },
  infoText: {
    fontSize: 14.5,
    color: CAT_TEXT,
    lineHeight: 1.7,
  },

  // ── Sub-accordions inside steps
  subAccordion: {
    borderRadius: '8px !important',
    border: `1px solid ${CAT_BORDER} !important`,
    boxShadow: 'none !important',
    marginBottom: '8px !important',
    '&:before': { display: 'none !important' },
    '&.Mui-expanded': {
      borderColor: `${CAT_YELLOW} !important`,
      marginBottom: '8px !important',
    },
  },
  subAccordionSummary: {
    fontWeight: 600,
    fontSize: 14,
    color: CAT_BLACK,
  },
  subAccordionDetails: {
    paddingTop: 0,
    paddingBottom: 12,
  },

  // ── Code block
  codeBlock: {
    background: CAT_BLACK,
    color: '#A5F3FC',
    borderRadius: 8,
    padding: '14px 18px',
    fontFamily: 'monospace',
    fontSize: 13,
    lineHeight: 1.75,
    position: 'relative',
    overflowX: 'auto',
    marginTop: 8,
    marginBottom: 8,
  },
  codeCopyBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    color: '#9E9E9E',
    padding: 4,
    '&:hover': { color: CAT_YELLOW },
  },

  // ── Client type chips
  chipRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  chipActive: {
    background: CAT_YELLOW,
    color: CAT_BLACK,
    fontWeight: 700,
    border: `1.5px solid ${CAT_YELLOW}`,
    '&:hover': { background: '#FFD740' },
    '&:focus': { background: CAT_YELLOW },
  },
  chipInactive: {
    background: 'transparent',
    border: `1.5px solid ${CAT_BORDER}`,
    color: CAT_TEXT,
    fontWeight: 500,
    '&:hover': { borderColor: CAT_YELLOW, color: CAT_BLACK },
    '&:focus': { background: 'transparent' },
  },

  // ── Form select
  formControl: {
    minWidth: 240,
    marginBottom: 16,
  },
  selectRoot: {
    borderRadius: 8,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: CAT_YELLOW,
    },
  },

  // ── Formula box
  formulaBox: {
    background: CAT_BLACK,
    borderRadius: 12,
    padding: '18px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 16,
  },
  formulaPart: {
    color: CAT_YELLOW,
    fontWeight: 700,
    fontSize: 15,
  },
  formulaOp: {
    color: '#9E9E9E',
    fontWeight: 700,
    fontSize: 18,
  },

  // ── POC / issues box
  pocBox: {
    background: CAT_GRAY,
    border: `1px solid ${CAT_BORDER}`,
    borderRadius: 10,
    padding: '14px 20px',
    marginTop: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  pocTitle: {
    fontWeight: 700,
    fontSize: 13,
    color: CAT_BLACK,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  pocItem: {
    fontSize: 13.5,
    color: CAT_TEXT,
  },

  // ── Issue badge
  issueBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#FFF8E1',
    border: '1px solid #FFCC02',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: 13,
    color: '#7A5F00',
    marginBottom: 6,
    marginRight: 6,
  },

  // ── Screenshot placeholder
  screenshotPlaceholder: {
    background: '#F9F9F9',
    border: `2px dashed ${CAT_BORDER}`,
    borderRadius: 12,
    padding: '36px 24px',
    textAlign: 'center',
    color: '#BDBDBD',
    fontSize: 14,
    marginTop: 12,
    marginBottom: 12,
  },

  // ── Step nav bar
  stepNavBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 24,
    paddingTop: 20,
    borderTop: `1px solid ${CAT_BORDER}`,
  },
  stepNavBtn: {
    background: CAT_YELLOW,
    color: CAT_BLACK,
    fontWeight: 700,
    borderRadius: 8,
    padding: '8px 24px',
    textTransform: 'none',
    '&:hover': { background: '#FFD740' },
    '&.Mui-disabled': { background: CAT_BORDER, color: '#9E9E9E' },
  },
  stepNavBtnOutline: {
    border: `1.5px solid ${CAT_BORDER}`,
    color: CAT_TEXT,
    fontWeight: 600,
    borderRadius: 8,
    padding: '8px 24px',
    textTransform: 'none',
    background: 'transparent',
    '&:hover': { borderColor: CAT_YELLOW, color: CAT_BLACK },
  },

  // ── Validation step
  validationSuccess: {
    background: 'linear-gradient(135deg, #E8F5E9, #F1F8E9)',
    border: '1.5px solid #A5D6A7',
    borderRadius: 12,
    padding: '24px',
    textAlign: 'center',
  },
  validationIcon: {
    fontSize: 56,
    color: '#4CAF50',
    marginBottom: 8,
  },

  // ── Divider with label
  dividerLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '24px 0',
    '& span': {
      fontSize: 12,
      color: '#BDBDBD',
      fontWeight: 600,
      letterSpacing: 1,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    },
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: CAT_BORDER,
  },
}));


